import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import FlashOfferSummary from './FlashOfferSummary'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { getOfferExtra, OFFER_VIEWS_24_HOURS_THRESHOLD } from 'selectors/offerExtraSelector'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOfferExtraById } from 'actions/OfferActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import {
  getCheapestSelectedPackageOptionWithDefault,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import { fetchCruiseLines } from 'actions/CruiseActions'
import FlashHotelPropertyDetails from './FlashHotelPropertyDetails'
import ContactUSP from '../ContactUSP'
import FlashHotelUSPs from './FlashHotelUSPs'
import Divider from 'components/Luxkit/Divider'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import FlashHotelViewsUrgencyBanner from './FlashHotelViewsUrgencyBanner'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferDescription from '../OfferDescription'
import OfferPageIntroInclusions from '../OfferPageIntroInclusions'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import TextLink from 'components/Luxkit/TextLink'
import ModalContext from 'contexts/ModalContext'
import FlashHotelOfferDescription from './FlashHotelMobileVariant/FlashHotelOfferDescription'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FlashHotelInclusionsAndHighlightsModal from './FlashHotelMobileVariant/Modals/FlashHotelInclusionsAndHighlightsModal'
import FlashOfferIntroPricingBlock from './FlashHotelMobileVariant/FlashOfferIntroPricingBlock'
import config from 'constants/config'
import { EmptyArray } from 'lib/array/arrayUtils'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import FlashOfferWithFlightsSummary from './FlashOfferWithFlightsSummary'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const HotelFlightsSummary = styled(FlashOfferWithFlightsSummary)`
  min-width: ${rem(382)};
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  checkIn?: string;
  offer: App.HotelOffer;
  className?: string;
  onViewPackageOptions: () => void;
  flightDestination?: App.Airport;
  offerExtra: App.OfferExtra;
  shouldShowSalesPhoneUSP: boolean;
  cruiseLines?: Array<App.CruiseLine>;
  shareModalConfig: ReturnType<typeof getShareModalConfig>;
  flightOrigin?: App.Airport;
}

function FlashHotelIntro(props: Props) {
  const {
    checkIn,
    offer,
    className,
    onViewPackageOptions,
    flightDestination,
    offerExtra,
    shouldShowSalesPhoneUSP,
    cruiseLines = EmptyArray,
    shareModalConfig,
    flightOrigin,
    cheapestOption,
  } = props

  const property = offer.property
  const showModal = useContext(ModalContext)
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const isUrgencyTodayActive = offerExtra && offerExtra.viewsInLast24Hours >= OFFER_VIEWS_24_HOURS_THRESHOLD
  const isMobileDevice = useIsMobileDevice()
  const isHotelFlightsToggleV2Enabled = useOptimizelyExperiment(OptimizelyExperiments.croHotelFlightsToggleV2Enabled, !isMobileDevice)

  const hasFlights = !!cheapestOption.flightPrice && !offer.flightCacheDisabled
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'
  const showHotelPrice = !offer.bundledWithFlightsOnly || (offer.bundledWithFlightsOnly && flightsEnabled && !hideNoCacheMessaging && hasFlights)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const availableForPurchase = useIsAvailableForPurchase()

  const showHotelFlightsV2Summary = availableForPurchase && isHotelFlightsToggleV2Enabled && !isMobileDevice && flightsEnabled && !hideNoCacheMessaging && !offer.bundledWithFlightsOnly

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  useEffect(() => {
    dispatch(fetchCruiseLines())
  }, [dispatch])

  const openHighlightsAndInclusions = useCallback(async() => {
    showModal(
      <FlashHotelInclusionsAndHighlightsModal
        highlightsMarkdown={offer.highlights}
        additionalDescriptionMarkdown={offer.additionalDescription}
        tileInclusions={offer.tileInclusions}
      />,
    )
  }, [offer, showModal])

  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <Group direction="vertical" gap={20}>
        <CSSBreakpoint min="tablet">
          <OfferPageLabelGroup offer={offer} showLimitedAvailability />
        </CSSBreakpoint>
        <FlashHotelPropertyDetails
          property={property}
          offer={offer}
          cruiseLines={cruiseLines}
          shareModalConfig={shareModalConfig}
        />
        {isMobileDevice && <>
          {showHotelPrice && <FlashOfferIntroPricingBlock />}
          {offer.tileInclusions && offer.tileInclusions.length > 2 && <>
            <Divider kind="primary" />
            <Group direction="vertical" gap={8}>
              <BodyText variant="large" weight="bold">Your handpicked inclusions:</BodyText>
              <OfferPageIntroInclusions tileInclusionsList={offer.tileInclusions} />
            </Group>
            <TextLink size="medium" onClick={openHighlightsAndInclusions}>View all highlights & inclusions</TextLink>
          </>}
          {offer.description && <>
            <Divider kind="primary"/>
            <FlashHotelOfferDescription descriptionMarkdown={offer.description} facilitiesMarkdown={offer.facilities}/>
          </>}
        </>}
        {!isMobileDevice && <>
          {offer.description && <OfferDescription description={offer.description} />}
          <SoftOfferPageWalledGarden>
            {offer.additionalDescription && <>
              <Divider kind="primary"/>
              <MarkdownRender type="compact" content={offer.additionalDescription} />
            </>}
          </SoftOfferPageWalledGarden>
        </>}
        <SoftOfferPageWalledGarden>
          <Divider kind="primary" />
          <FlashHotelUSPs offer={offer} checkIn={checkIn} />
          <Divider kind="primary" />
          {shouldShowSalesPhoneUSP && <ContactUSP />}
        </SoftOfferPageWalledGarden>
      </Group>
      <SummaryColumn>
        <SummaryBlock>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareButton outdent={isExportMediaEnabled ? undefined : 'right'} />
                </ShareModal>
              </TripGuard>
              {isExportMediaEnabled && <TripGuard inverse>
                <ExportMediaContentButton href={mediaExportURL} target="_blank"/>
              </TripGuard>}
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareButton />
                </ShareModal>
              </TripGuard>
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
              {isExportMediaEnabled && <TripGuard>
                <ExportMediaContentButton href={mediaExportURL} target="_blank"/>
              </TripGuard>}
            </Group>
          </CSSBreakpoint>
          <div>
            {isUrgencyTodayActive && <FlashHotelViewsUrgencyBanner numberOfViews={offerExtra.viewsInLast24Hours} />}
            {!showHotelFlightsV2Summary &&
              <HotelSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />
            }
            {showHotelFlightsV2Summary &&
              <HotelFlightsSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />
            }
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 1000),
  cheapestOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  checkIn: state.checkInDate,
  offer: state.offer,
  flightOrigin: state.airport,
  offerExtra: getOfferExtra(appState, state.offer?.id),
  cruiseLines: appState.cruise.cruiseLines,
}))(FlashHotelIntro)
